import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import moment from 'moment';
import { dateValidator } from '../../custom-validator/date-validator';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { CustomDateAdapter } from '../../module/custom-date-adapter';
import { MatCalendar, MatDatepicker, MatDatepickerIntl } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY', // Parsing format
  },
  display: {
    dateInput: 'DD.MM.YYYY', // Input field format
    monthYearLabel: 'MMMM YYYY', // Month-year label
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class DatePickerComponent implements OnInit, OnChanges {
  @ViewChild('calendar') calendar!: MatCalendar<Date>;
  @Output() dateChanged = new EventEmitter<any>();
  @Input('startAtDate') startAtDate: any;
  @Input('minDate') minDate?: Date;
  @Input('maxDate') maxDate?: Date;
  @Input('selectedDate') selectedDate: any;
  @Input('canEditDate') canEditDate: boolean = true;
  @Input('width') width: string;
  @Input('dateLabel') dateLabel: string;
  @Input('datePlaceHol') datePlaceHol: string;
  @Input('editDate') editDate: any;
  @Input('currentLang') currentLang: string;
  @Input() isRequired: boolean = false; // add this input
  @ViewChild(MatDatepicker) dp!: MatDatepicker<Date>;
  isValidDate:any = true
  date = new FormControl('', [Validators.required, dateValidator()]);
  momentDate = new FormControl<moment.Moment | null>(null);
  minimumDate: any = moment();
  currentDate: any = new Date();
  currentLanguage: string; //for providing real time translation for mat datepicker.
  currentMonth: Date = new Date();
  private isInitialSetup = true;
  constructor(
    private translateService: TranslateService,
    private _adapter: DateAdapter<any>,
    private _intl: MatDatepickerIntl,
    @Inject(MAT_DATE_LOCALE) private _locale: string
  ) {    
    // this.momentDate.setValue(this.selectedDate); // Initial setup; will remove the comment later
    this.momentDate.valueChanges.subscribe(() => {
      if (this.isInitialSetup && this.selectedDate) {
        this.isInitialSetup = false;
        return;
      }
      // if(this.component === 'holiday'){
      //   this.dateChange()
      // }
      this.dateChange()
      this.onDateChange(this.momentDate);
    });
  }
  ngOnInit(): void {
    this.prepareEditDate();
    this.applyValidators();
    if (this.canEditDate === false) {// to disabled the datepicker for readonly mode.
      this.momentDate.disable();
    } else {
      this.momentDate.enable();
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    this.currentLanguage = this.currentLang;
    this._locale = this.currentLanguage;
    this.handleTranslation();
    // 🧠 Skip DOM-changing logic while datepicker is open
    if (this.dp && this.dp.opened) return;

    if (changes['minDate'] || changes['maxDate']) {
      this.minimumDate = this.minDate ? moment(this.minDate) : null;
    }
    // Only initialize edit date once in edit mode
    if (changes['editDate'] && changes['editDate'].currentValue && !changes['editDate'].previousValue) {
      this.prepareEditDate();
    }
    this.applyValidators();
  }

  prepareEditDate() {
    const momentDate = moment(this.editDate, 'DD.MM.YYYY');
    if (this.editDate) {
      this.momentDate.setValue(momentDate);
      this.date.setValue(this.editDate);
    }
  }

  private applyValidators(): void {
    if (this.isRequired) {
      this.momentDate.setValidators([Validators.required]);
    } else {
      this.momentDate.clearValidators();
    }
    this.momentDate.updateValueAndValidity();
  }
  onDateChange(seleDate: any) {
    if (!seleDate.value) {
      this.date.setValue('');
      this.isValidDate = true;
      this.isRequired ? this.momentDate.setErrors({ invalidDate: true }): this.momentDate.setErrors(null);
      this.dateChanged.emit({ valid: false, date: '' });
      return;
    }

    // const formattedDate = moment(seleDate.value).format('DD.MM.YYYY');
    let raw = seleDate.value; // e.g., "05.1"
    let formattedDate = '00.00.0000'; // default
    // Case 1: Moment object
    if (moment.isMoment(seleDate.value)) {
      raw = (seleDate.value as any)?._i || "";
    }

    if (seleDate.value?._i && typeof seleDate.value?._i === 'object') {
      // If it's not a Moment but has _i structure
      const i = seleDate.value._i;
      raw = `${String(i.date).padStart(2, '0')}.${String(i.month + 1).padStart(2, '0')}.${i.year}`;
    }  
    // else {
    //   console.log('3');
      
    //   raw = "";
    // }
    const parts = raw.split('.'); // Now ALWAYS safe
    let dd = (parts[0] || '').padEnd(2, '0');
    let mm = (parts[1] || '').padEnd(2, '0');
    let yyyy = (parts[2] || '').padEnd(4, '0');
    // Final output
    formattedDate = `${dd}.${mm}.${yyyy}`;
    this.date.setValue(formattedDate);
    this.dateChange(); // validate after formatting
    this.dateChanged.emit({ valid: this.isValidDate, date: formattedDate });
  }

  handleTranslation() {
    // for providing translation to mat datepicker -starts here
    this._adapter.setLocale(this._locale);
  }

  addClickListener() {
    // Wait for the calendar to render, then add event listener
    setTimeout(() => {
      const calendarCells = document.querySelectorAll(
        '.mat-calendar-body-cell'
      );
      calendarCells.forEach((cell) => {
        cell.addEventListener('click', () => {
          const cellText = cell.textContent?.trim();
          if (cellText) {
            const clickedDate = new Date(this.calendar.activeDate);
            clickedDate.setDate(parseInt(cellText, 10));
            // Toggle selection
            if (
              this.selectedDate &&
              this.isSameDate(this.selectedDate, clickedDate)
            ) {
              this.selectedDate = null; // Deselect
            } else {
              this.selectedDate = clickedDate; // Select new date
            }

            // Force calendar to update
            this.calendar.updateTodaysDate();
          }
        });
      });
    }, 100);
  }

  // Helper function to compare dates (ignoring time)
  isSameDate(d1: Date, d2: Date): boolean {
    let year, month, date;
    if (moment.isMoment(d1)) {
      year = d1.year(); // Get year
      month = d1.month(); // Get month (0-based, so add 1)
      date = d1.date(); // Get day
    } else {
      year = d1.getFullYear(); // Get year
      month = d1.getMonth(); // Get month (0-based, so add 1)
      date = d1.getDate(); // Get day
    }
    return (
      year === d2.getFullYear() &&
      month === d2.getMonth() &&
      date === d2.getDate()
    );
  }

  dateChange(){
    const min = this.minDate ? moment(this.minDate, 'DD.MM.YYYY') : null;
    const max = this.maxDate ? moment(this.maxDate, 'DD.MM.YYYY') : null;
    const selected = moment(this.momentDate.value).startOf('day'); // ignore time
    if (!this.momentDate.value) {
      this.isValidDate = true;
      this.momentDate.setErrors(null);
      return;
    }
    const momemntDateValue = this.momentDate.value as moment.Moment;

    // Stop immediately if no value or invalid moment
    if (!momemntDateValue || !momemntDateValue.isValid()) {
      this.isValidDate = false;
      return;
    }

    // Extract the original raw input string safely
    const rawValue = (momemntDateValue as any)?._i; // Cast to any to avoid TS error

    // Check proper dd.mm.yyyy format
    const validPattern = /^\d{2}\.\d{2}\.\d{4}$/;

    if (!validPattern.test(rawValue)) {
      this.isValidDate = true;
      return;
    }

    // Check year length 4 digits
    const year = momemntDateValue.year();
    if (year.toString().length !== 4 || year < 1000) {
      this.isValidDate = true;
      return;
    }
    
    // ---------- Execute original range validation ----------
    if (min && max) {
      this.isValidDate = momemntDateValue.isBetween(min.startOf('day'), max.startOf('day'), undefined, '[]');
    } else if (min && !max) {
      this.isValidDate = momemntDateValue.isSameOrAfter(min.startOf('day'));
    } else if (!min && max) {
      this.isValidDate = momemntDateValue.isSameOrBefore(max.startOf('day'));
    }
    else {
      this.isValidDate = true;
    }
    if (!this.isValidDate) {
      this.momentDate.setErrors({ invalidDate: true });
    } else {
      this.momentDate.setErrors(null);
    }
  }
  ngAfterViewInit() {
    this.dp.openedStream.subscribe(() => {
      setTimeout(() => {
        const cells = document.querySelectorAll('.mat-calendar-body-cell');
        cells.forEach(cell => {
          cell.addEventListener('click', () => {
            // Allow selection click to propagate even after min/max updates
          });
        });
      }, 100);
    });

    // When overlay closes, ensure no residual state
    this.dp.closedStream.subscribe(() => {
      this.isInitialSetup = false;
    });
  }
}
